import type { Character, FilmProject, Scene, StoryboardFrame, StoryboardFrameSourceAssignments, UploadedAssetRecord } from "../types";
import { Icon } from "./Icon";

interface FrameSourceAssignmentsProps {
  project: FilmProject;
  scene: Scene;
  frame: StoryboardFrame;
  onChange: (assignments?: StoryboardFrameSourceAssignments) => void;
}

function sceneReferenceAssets(project: FilmProject, sceneId: string): UploadedAssetRecord[] {
  return (project.uploadedAssets?.sceneReferences || []).filter((asset) => asset.sceneId === sceneId && !asset.disabled);
}

function characterByName(project: FilmProject, name: string): Character | undefined {
  return project.characters.find((character) => character.name === name);
}

function defaultAssignments(project: FilmProject, scene: Scene): StoryboardFrameSourceAssignments {
  const references = sceneReferenceAssets(project, scene.id)
    .map((asset) => asset.assetId)
    .filter((assetId): assetId is string => Boolean(assetId));
  return {
    characterNames: scene.characters,
    voiceCharacterNames: scene.characters.filter((name) => {
      const character = characterByName(project, name);
      return Boolean(character && character.voiceStatus !== "not_needed" && !character.voiceDisabled);
    }),
    referenceAssetIds: references
  };
}

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function characterCardLabel(character?: Character): string {
  if (!character) return "Missing";
  if (character.cardDisabled) return "Disabled";
  if (character.cardSource === "seed_generated" || character.cardStatus === "generated") return "Seedance";
  if (character.cardStatus === "uploaded") return "Uploaded";
  return "Missing";
}

function voiceLabel(character?: Character): string {
  if (!character) return "Missing";
  if (character.voiceDisabled) return "Disabled";
  if (character.voiceStatus === "not_needed") return "No voice";
  if (character.voiceSource === "user_upload") return "User audio";
  if (character.voiceSource === "seed_generated") return character.voicePreset || "Seedance";
  return character.voiceStatus === "assigned" ? "Assigned" : "Missing";
}

function referenceLabel(asset: UploadedAssetRecord): string {
  if (asset.sourceMode === "seed_generated" || asset.provider === "evolink" || asset.provider === "seedance" || asset.provider === "seed") {
    return "Seedance";
  }
  if (asset.parseStatus === "parsed") return "Parsed";
  return "Uploaded";
}

export function FrameSourceAssignments({ project, scene, frame, onChange }: FrameSourceAssignmentsProps) {
  const inherited = !frame.sourceAssignments;
  const assignments = frame.sourceAssignments || defaultAssignments(project, scene);
  const references = sceneReferenceAssets(project, scene.id);
  const characterNames = scene.characters;
  const activeCharacterCount = assignments.characterNames.length;
  const activeVoiceCount = assignments.voiceCharacterNames.length;
  const activeReferenceCount = assignments.referenceAssetIds.length;

  function update(changes: Partial<StoryboardFrameSourceAssignments>) {
    onChange({ ...assignments, ...changes });
  }

  return (
    <section className="frame-source-assignments" aria-label="Frame source assignments">
      <header>
        <div>
          <span>Frame Sources</span>
          <strong>{inherited ? "Using scene defaults" : "Custom board sources"}</strong>
          <small>{activeCharacterCount} character{activeCharacterCount === 1 ? "" : "s"} · {activeVoiceCount} voice{activeVoiceCount === 1 ? "" : "s"} · {activeReferenceCount} reference{activeReferenceCount === 1 ? "" : "s"}</small>
        </div>
        <button className="secondary-action compact-action" disabled={inherited} onClick={() => onChange(undefined)} type="button">
          Reset
        </button>
      </header>

      <div className="frame-source-grid">
        <div className="frame-source-column">
          <span>Characters</span>
          {characterNames.length ? characterNames.map((name) => {
            const character = characterByName(project, name);
            const checked = assignments.characterNames.includes(name);
            return (
              <label className={!checked ? "excluded" : ""} key={name}>
                <input
                  checked={checked}
                  onChange={() => update({ characterNames: toggleValue(assignments.characterNames, name) })}
                  type="checkbox"
                />
                <span>
                  <strong>{name}</strong>
                  <small>{character?.role || "Scene character"} · {characterCardLabel(character)}</small>
                </span>
              </label>
            );
          }) : (
            <p>No scene characters.</p>
          )}
        </div>

        <div className="frame-source-column">
          <span>Voices</span>
          {characterNames.length ? characterNames.map((name) => {
            const character = characterByName(project, name);
            const disabled = character?.voiceStatus === "not_needed" || character?.voiceDisabled;
            const checked = assignments.voiceCharacterNames.includes(name);
            return (
              <label className={!checked || disabled ? "excluded" : ""} key={name}>
                <input
                  checked={checked}
                  disabled={disabled}
                  onChange={() => update({ voiceCharacterNames: toggleValue(assignments.voiceCharacterNames, name) })}
                  type="checkbox"
                />
                <span>
                  <strong>{name}</strong>
                  <small>{voiceLabel(character)}</small>
                </span>
              </label>
            );
          }) : (
            <p>No voice lanes for this scene.</p>
          )}
        </div>

        <div className="frame-source-column">
          <span>References</span>
          {references.length ? references.map((asset) => {
            const assetId = asset.assetId || "";
            const checked = assetId ? assignments.referenceAssetIds.includes(assetId) : false;
            return (
              <label className={!checked ? "excluded" : ""} key={assetId || asset.label}>
                <input
                  checked={checked}
                  disabled={!assetId}
                  onChange={() => assetId && update({ referenceAssetIds: toggleValue(assignments.referenceAssetIds, assetId) })}
                  type="checkbox"
                />
                <span>
                  <strong>{asset.label}</strong>
                  <small>{referenceLabel(asset)} · {asset.fileName}</small>
                </span>
              </label>
            );
          }) : (
            <p>
              <Icon name="image" />
              Add a scene reference from References or Asset Library.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
